/**
 * buildFreshness — notice a deploy without asking the service worker.
 *
 * `swUpdate.ts` already re-checks for a new worker on load, on a five-minute
 * interval, and on every "the user came back" signal. All of that goes through
 * `registration.update()`, which re-fetches `/sw.js` — and on karr.pro the
 * browser answers that from its own HTTP cache for **four hours**.
 *
 * Not a bug in this repo. `public/_headers` asks for `max-age=0` on `/sw.js`
 * and every other header in that file applies; the Cache-Control is overridden
 * at the zone by Cloudflare's Browser Cache TTL, which only the dashboard can
 * change. Measured 22 Aug 2026: `max-age=14400` on the custom domain,
 * `max-age=0` on the same rule at *.pages.dev.
 *
 * `updateViaCache: 'none'` on the registration is the direct answer and is
 * applied in `swUpdate.ts`. This file is the second line, because it does not
 * involve the worker at all:
 *
 *   - the shell this tab is RUNNING names its entry chunk in the DOM, in the
 *     `<script type="module">` the browser is executing right now;
 *   - the shell the NETWORK would serve names its own in `index.html`;
 *   - if those two differ, this tab is a build behind, whatever the worker
 *     believes.
 *
 * Nothing new is emitted at build time. Vite already fingerprints the entry
 * (`assets/index-CNzdBNSH.js`) and already writes it into `index.html`, so the
 * version marker exists and is guaranteed to change exactly when the code does.
 *
 * ## Why the old heal never fired
 *
 * `chunkReload.ts` recovers when a chunk FAILS to load. That is the deploy
 * where the old files are gone. It cannot see this one: the old chunks are
 * still on the CDN and load perfectly, so nothing throws, nothing is detected,
 * and the tab runs last week's code indefinitely — which cost three rounds of
 * debugging on 26 Aug 2026, chasing a bug that had already been fixed and
 * deployed.
 *
 * @since 1.0.0 — fresh
 */

/** The entry chunk name only, so a different origin or a proxy cannot make two
 *  identical builds look different. */
const ENTRY_RE = /assets\/(index-[A-Za-z0-9_-]+\.js)/;

/** The entry chunk named inside a served `index.html`. */
export function entryFromHtml(html: string): string | null {
    return ENTRY_RE.exec(html)?.[1] ?? null;
}

/**
 * The entry chunk THIS tab is running, read from the live DOM rather than from
 * `import.meta.url`: Rollup is free to put any given module in any chunk, but
 * the `<script type="module">` the browser is executing is the shell by
 * definition.
 */
export function runningEntry(doc: Document = document): string | null {
    const scripts = Array.from(doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'));
    for (const s of scripts) {
        const hit = ENTRY_RE.exec(s.getAttribute('src') ?? '');
        if (hit) return hit[1];
    }
    return null;
}

/**
 * Stale only when both names are known AND they differ.
 *
 * An unknown on either side is NOT staleness. A missing entry means the page
 * was served by something this does not understand — a dev server, a proxy, a
 * captive portal — and reloading on that guess would be a reload loop with no
 * way out. Silence is the safe answer to a question that could not be asked.
 */
export function isStaleBuild(running: string | null, network: string | null): boolean {
    if (!running || !network) return false;
    return running !== network;
}

/**
 * Ask the network which shell it would serve now.
 *
 * Two things keep the caches out of this:
 *   - a cache-busting query, because the service worker PRECACHES `index.html`
 *     and would otherwise answer with the very shell we are trying to test. A
 *     query string does not match the precache entry, so the request falls
 *     through to the network;
 *   - `cache: 'no-store'`, which bypasses the browser's HTTP cache and so the
 *     zone-level Browser Cache TTL along with it.
 *
 * Returns null on any failure. Offline, a flaky connection and a 502 are all
 * "no answer", never "you are stale".
 */
export async function fetchNetworkEntry(now: number = Date.now()): Promise<string | null> {
    try {
        const res = await fetch(`/index.html?_fresh=${now}`, { cache: 'no-store' });
        if (!res.ok) return null;
        return entryFromHtml(await res.text());
    } catch {
        return null;
    }
}

/** True when the network is serving a different build from the one running. */
export async function isRunningStaleBuild(): Promise<boolean> {
    const running = runningEntry();
    if (!running) return false; // nothing to compare — do not guess
    return isStaleBuild(running, await fetchNetworkEntry());
}
